/**
 * AttachOperationsModal — "Attach Vehicles & Operations" compose/preview modal
 * (PRD `FeaturePlans/OpsUpload/prd-fixed-route-vehicles-and-operations.md`,
 * Phase 1 item 5 / Modals 2 & 3, GAP B/C/E/G).
 *
 * For a selected fixed route, this modal lets a user compose an operations plan
 * that is assigned onto the routes:
 *   1. Pick an Operation solution (read-only `getoperationssolutionsall` via
 *      `fetchOperationSolutionContexts`).
 *   2. Map / select vehicle slots (Agent B's generalised `VehicleMappingPanel`).
 *   3. Per vehicle, set day-of-week (default from `route.serviceDays`), start/end
 *      time, direction ('forward' | 'reverse'), shiftType ('timetable' |
 *      'shuttle'), startStopIndex, firstStopOffsetMins, numberOfRepeats.
 *   4. PREVIEW the shift solutions that WOULD be created (route version x vehicle
 *      x operating day) using the corrected `DatedShiftSolutionRequest` shape.
 *
 * The plan is assigned onto the route via `FixedRoute.operationAssignments`
 * (Agent A) using the existing context `updateRoute()`. NOTHING is written to
 * Ops here: the plan/route versions reach Ops later, when you upload these
 * routes (Data Transfer → Routes).
 *
 * Per repo convention every categorical/list/settings label leads with a
 * semantic MUI (Outlined) icon, and all date/time composition routes through
 * `dateTimeUtils` (see `attachOperations/draftPlan.ts`).
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import AltRouteOutlined from '@mui/icons-material/AltRouteOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DirectionsBusOutlined from '@mui/icons-material/DirectionsBusOutlined';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import PreviewOutlined from '@mui/icons-material/PreviewOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';

import {
  fetchNetworks,
  fetchOperationSolutionContexts,
  type Environment,
  type OpsOperationSolutionContext,
  type ShiftSolutionDirection,
  type ShiftSolutionType,
  type UATNetwork,
} from '../../services/fixedRouteUatClient';
import {
  ENVIRONMENT_OPTIONS,
  getEnvironmentDisplayName,
  getEnvironmentTokenStorageKey,
} from '../../services/environmentConfig';
import { trackAnalyticsEvent } from '../../services/analytics';
import { useFixedRoutes } from '../../context/FixedRoutesContext';
import type {
  DraftVehicleAssignment,
  FixedRoute,
  RouteOperationAssignment,
  ServiceDaysMask,
} from '../../types/fixedRoutes';
import { extractServiceDate, extractTimeFromDateTime } from '../../utils/dateTimeUtils';
import { VehicleMappingPanel } from './VehicleMappingPanel';
import type { VehicleSlot } from '../../services/fixedRoute/vehicleMappingTypes';
import {
  SERVICE_DAY_KEYS,
  SERVICE_DAY_SHORT_LABELS,
  buildDefaultVehicleAssignment,
  collapsePreviewRows,
  describeServiceDays,
  expandDraftToPreview,
  weekdayServiceDaysMask,
} from './attachOperations/draftPlan';

// ─── Props ──────────────────────────────────────────────────────────────────

export interface AttachOperationsModalProps {
  open: boolean;
  onClose: () => void;
  /** The route the draft plan is being composed for. */
  route: FixedRoute | null;
  /** Pre-select an environment (default 'UAT'). */
  defaultEnvironment?: Environment;
  /**
   * Optional seeds for the "Operation" section, applied on the open transition so
   * the user doesn't re-enter / re-load when launching from the Data Transfer
   * drawer (which already has these). Mid-session edits are never clobbered.
   */
  seedJwtToken?: string;
  seedEnvironment?: Environment;
  seedNetworks?: UATNetwork[];
  seedProductId?: string;
}

const DIRECTION_OPTIONS: ShiftSolutionDirection[] = ['forward', 'reverse'];
const SHIFT_TYPE_OPTIONS: ShiftSolutionType[] = ['timetable', 'shuttle'];

// This Dialog is hoisted to `theme.zIndex.modal + 1300` (= 2600) so it clears
// the Data Transfer drawer it can be launched from. MUI Select/Menu/Popover
// popups default to `theme.zIndex.modal` (1300), which would render them BEHIND
// this dialog and make them invisible/unusable. Raise every nested popup ONE
// step further (modal + 1400 = 2700) so it sits above the dialog. Keep this
// value above the dialog's `+ 1300` if either ever changes; it is also passed
// down to the embedded VehicleMappingPanel so its slot dropdowns clear the
// dialog too.
const MODAL_POPPER_SX = { zIndex: (t: Theme) => t.zIndex.modal + 1400 };

/**
 * The per-route schedule fields, minus the vehicle identity. In "same settings
 * for all" mode one of these is applied to every route; in individual mode each
 * route gets its own.
 */
type SharedScheduleSettings = Omit<
  DraftVehicleAssignment,
  'opsVehicleId' | 'opsVehicleLabel' | 'cartographerVehicleId' | 'driverId'
>;

function defaultSharedSettings(route?: FixedRoute | null): SharedScheduleSettings {
  const base = buildDefaultVehicleAssignment(
    route ?? { startTime: '', endTime: undefined, serviceDays: undefined },
    { id: '' },
  );
  return {
    direction: base.direction,
    startStopIndex: base.startStopIndex,
    firstStopOffsetMins: base.firstStopOffsetMins,
    numberOfRepeats: base.numberOfRepeats,
    shiftType: base.shiftType,
    startTime: base.startTime,
    endTime: base.endTime,
    serviceDays: base.serviceDays,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AttachOperationsModal: React.FC<AttachOperationsModalProps> = ({
  open,
  onClose,
  route,
  defaultEnvironment = 'UAT',
  seedJwtToken,
  seedEnvironment,
  seedNetworks,
  seedProductId,
}) => {
  const theme = useTheme();
  const { updateRoute, routes } = useFixedRoutes();

  // This modal composes drafts across ALL fixed routes in one screen: one slot
  // per route, each mapped to a vehicle with its own operating schedule. The
  // `route` prop is retained only as the initial anchor for the service date.
  const slotIdForRoute = (routeId: string) => `route:${routeId}`;
  const routeIdFromSlot = (slot: { localId: string }): string | undefined =>
    slot.localId.startsWith('route:') ? slot.localId.slice('route:'.length) : undefined;

  // ── Auth + network (for the read-only operation fetch) ───────────────────────
  const [environment, setEnvironment] = React.useState<Environment>(defaultEnvironment);
  const [jwtToken, setJwtToken] = React.useState('');
  const [showJwt, setShowJwt] = React.useState(false);
  const jwtStorageKey = getEnvironmentTokenStorageKey(environment);

  const [networks, setNetworks] = React.useState<UATNetwork[]>([]);
  const [networksLoading, setNetworksLoading] = React.useState(false);
  const [selectedProductId, setSelectedProductId] = React.useState('');

  // ── Operation fetch ──────────────────────────────────────────────────────────
  // Plan date range — start (serviceDate, also the operation-fetch date) and end,
  // applied to ALL vehicles/shifts/operations in this plan.
  const [serviceDate, setServiceDate] = React.useState('');
  const [planEndDate, setPlanEndDate] = React.useState('');
  const [operations, setOperations] = React.useState<OpsOperationSolutionContext[]>([]);
  const [operationsLoading, setOperationsLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState<string | null>(null);
  const [selectedOperationId, setSelectedOperationId] = React.useState('');

  // ── Vehicle slots (one per route, mapped via VehicleMappingPanel) ──────────────
  const [slots, setSlots] = React.useState<VehicleSlot[]>([]);

  // ── Scheduling: one shared template ("same settings for all") + optional
  //    per-route overrides used only in individual mode. ───────────────────────────
  const [sameForAll, setSameForAll] = React.useState(true);
  const [shared, setShared] = React.useState<SharedScheduleSettings>(() => defaultSharedSettings());
  const [schedulesByRouteId, setSchedulesByRouteId] = React.useState<
    Record<string, DraftVehicleAssignment>
  >({});
  const [saveNotice, setSaveNotice] = React.useState<string | null>(null);

  const selectedNetwork = networks.find((n) => n.productId === selectedProductId);
  const selectedOperation = operations.find((o) => o.operationSolutionId === selectedOperationId);

  // Seed slots (one per route) + per-route schedules when the modal opens.
  React.useEffect(() => {
    if (!open || routes.length === 0) return;

    // Plan date range: prefer a previously-saved draft's range, then the anchor
    // route's GTFS range, else blank. Applied to all vehicles/shifts.
    const anchorRoute =
      (route && routes.find((r) => r.id === route.id)) ||
      routes.find((r) => r.serviceDateRange?.startDate) ||
      routes[0];
    const savedRange = routes
      .map((r) => r.operationAssignments?.[0]?.dateRange)
      .find(Boolean);
    const startSeed =
      savedRange?.startDate || anchorRoute?.serviceDateRange?.startDate || '';
    const endSeed =
      savedRange?.endDate || anchorRoute?.serviceDateRange?.endDate || startSeed;
    setServiceDate(startSeed ? extractServiceDate(startSeed) : '');
    setPlanEndDate(endSeed ? extractServiceDate(endSeed) : '');

    // Rehydrate the operation id from any route that already has a draft.
    const restoredOperationId =
      routes.map((r) => r.operationAssignments?.[0]?.operationSolutionId).find(Boolean) ?? '';
    setSelectedOperationId(restoredOperationId);

    const seededSlots: VehicleSlot[] = [];
    const seededSchedules: Record<string, DraftVehicleAssignment> = {};

    // Seed one slot per route in ascending name order (numeric + case-insensitive)
    // so the downstream mapping table, schedule cards, and preview — all of which
    // derive their order from `slots` — render ascending too. Spread-copy first so
    // we never mutate the context `routes` array in place.
    const sortedRoutes = [...routes].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }),
    );

    for (const r of sortedRoutes) {
      const draftVeh = r.operationAssignments?.[0]?.vehicleAssignments?.[0];
      // Only seed from a genuine preserved Ops *vehicle* id (not the shift id).
      const preservedVehicleId =
        draftVeh?.opsVehicleId ||
        r.simulationImport?.opsVehicleId ||
        r.uatMetadata?.opsVehicleId ||
        '';
      const preservedVehicleLabel =
        r.simulationImport?.opsVehicleLabel || r.uatMetadata?.opsVehicleLabel || '';

      seededSlots.push({
        localId: slotIdForRoute(r.id),
        name: r.name,
        startWindow: draftVeh?.startTime || r.startTime || '',
        endWindow: draftVeh?.endTime || r.endTime || '',
        opsVehicleId: preservedVehicleId,
        // Label resolves to rego + seating on vehicle load; never the bare id.
        opsVehicleLabel: preservedVehicleId ? preservedVehicleLabel || preservedVehicleId : '',
        // Auto-match key = the vehicle THIS ROUTE used in the sim (rego/name),
        // so it binds to its own Ops vehicle rather than matching the route name.
        vehicleMatchHint: preservedVehicleLabel || undefined,
      });

      if (draftVeh) seededSchedules[r.id] = draftVeh;
    }

    setSlots(seededSlots);
    setSchedulesByRouteId(seededSchedules);
    // Shared template: from a previously-saved draft's first assignment, else
    // the anchor route's defaults. Always start in "same settings for all" mode.
    const restoredAssignment = routes
      .map((r) => r.operationAssignments?.[0]?.vehicleAssignments?.[0])
      .find(Boolean);
    if (restoredAssignment) {
      const { opsVehicleId: _ignore, cartographerVehicleId: _c, driverId: _d, ...rest } =
        restoredAssignment;
      setShared(rest);
    } else {
      setShared(defaultSharedSettings(anchorRoute));
    }
    setSameForAll(true);
    setSaveNotice(null);
    setFetchError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, route?.id, routes]);

  // Seed the Operation section (JWT / environment / networks / product) from the
  // launching surface on the OPEN transition only, so the user doesn't re-enter a
  // token or re-click "Load networks" when launched from the Data Transfer drawer.
  // We never re-apply while open, so mid-session edits are preserved. This runs
  // before the localStorage hydration below would otherwise overwrite the JWT.
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;
    if (seedEnvironment) setEnvironment(seedEnvironment);
    if (seedNetworks && seedNetworks.length > 0) setNetworks(seedNetworks);
    if (seedProductId) setSelectedProductId(seedProductId);
    if (seedJwtToken && seedJwtToken.trim()) setJwtToken(seedJwtToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Hydrate JWT from localStorage when the env key changes — but never clobber a
  // seeded token applied on the open transition above.
  React.useEffect(() => {
    if (!open) return;
    if (seedJwtToken && seedJwtToken.trim()) return;
    const saved = localStorage.getItem(jwtStorageKey) || '';
    if (saved) setJwtToken(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtStorageKey, open]);

  function handleJwtChange(value: string) {
    setJwtToken(value);
    const trimmed = value.trim();
    if (trimmed) localStorage.setItem(jwtStorageKey, trimmed);
    else localStorage.removeItem(jwtStorageKey);
  }

  function handleEnvironmentChange(next: Environment) {
    setEnvironment(next);
    setJwtToken(localStorage.getItem(getEnvironmentTokenStorageKey(next)) || '');
    setNetworks([]);
    setSelectedProductId('');
    setOperations([]);
    setSelectedOperationId('');
    setFetchError(null);
  }

  const handleLoadNetworks = React.useCallback(async () => {
    if (!jwtToken.trim()) return;
    setNetworksLoading(true);
    setFetchError(null);
    try {
      const response = await fetchNetworks(jwtToken, environment);
      const list: UATNetwork[] = (response?.data?.entry ?? [])
        .slice()
        .sort((a, b) =>
          a.productName.localeCompare(b.productName, undefined, { sensitivity: 'base' }),
        );
      setNetworks(list);
      if (list.length === 0) setFetchError('No networks returned. Check the JWT token / environment.');
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load networks');
    } finally {
      setNetworksLoading(false);
    }
  }, [jwtToken, environment]);

  async function handleLoadOperations() {
    if (!jwtToken.trim() || !selectedProductId || !serviceDate) return;
    const net = networks.find((n) => n.productId === selectedProductId);
    const timezone = net?.timezone || 'Australia/Sydney';
    setOperationsLoading(true);
    setFetchError(null);
    setOperations([]);
    try {
      const contexts = await fetchOperationSolutionContexts(
        jwtToken,
        selectedProductId,
        extractServiceDate(serviceDate),
        timezone,
        environment,
      );
      setOperations(contexts);
      if (contexts.length === 0) {
        setFetchError('No operation solutions found for that network/date. Pick another date.');
      }
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load operation solutions');
    } finally {
      setOperationsLoading(false);
    }
  }

  // Route ids whose slot has a mapped vehicle (independent of operation choice).
  const mappedRouteIds = React.useMemo(() => {
    const ids: string[] = [];
    for (const slot of slots) {
      const rid = routeIdFromSlot(slot);
      if (rid && slot.opsVehicleId) ids.push(rid);
    }
    return ids;
  }, [slots]);

  const slotForRoute = React.useCallback(
    (routeId: string) => slots.find((s) => routeIdFromSlot(s) === routeId),
    [slots],
  );

  // The effective assignment for a route: the shared template in "same settings"
  // mode, else the route's own override (falling back to shared), always carrying
  // that route's mapped vehicle.
  const assignmentForRoute = React.useCallback(
    (routeId: string): DraftVehicleAssignment | null => {
      const slot = slotForRoute(routeId);
      if (!slot || !slot.opsVehicleId) return null;
      const tmpl: SharedScheduleSettings =
        !sameForAll && schedulesByRouteId[routeId] ? schedulesByRouteId[routeId] : shared;
      // Persist the resolved vehicle NAME (rego + seating) alongside the id so it
      // shows everywhere the plan is read back, not just inside this modal.
      return {
        ...tmpl,
        opsVehicleId: slot.opsVehicleId,
        opsVehicleLabel: slot.opsVehicleLabel || undefined,
      };
    },
    [slotForRoute, sameForAll, schedulesByRouteId, shared],
  );

  // ── Shared-template editing (same-settings mode) ──────────────────────────────
  function updateShared(patch: Partial<SharedScheduleSettings>) {
    setShared((prev) => ({ ...prev, ...patch }));
    setSaveNotice(null);
  }
  function toggleSharedDay(day: keyof ServiceDaysMask) {
    setShared((prev) => {
      const mask: ServiceDaysMask = { ...(prev.serviceDays ?? weekdayServiceDaysMask()) };
      mask[day] = !mask[day];
      return { ...prev, serviceDays: mask };
    });
    setSaveNotice(null);
  }

  // ── Per-route editing (individual mode) ───────────────────────────────────────
  function updateRouteSchedule(routeId: string, patch: Partial<DraftVehicleAssignment>) {
    setSchedulesByRouteId((prev) => {
      const slot = slotForRoute(routeId);
      const base = prev[routeId] ?? { ...shared, opsVehicleId: slot?.opsVehicleId ?? '' };
      return { ...prev, [routeId]: { ...base, ...patch } };
    });
    setSaveNotice(null);
  }
  function toggleRouteDay(routeId: string, day: keyof ServiceDaysMask) {
    setSchedulesByRouteId((prev) => {
      const slot = slotForRoute(routeId);
      const cur = prev[routeId] ?? { ...shared, opsVehicleId: slot?.opsVehicleId ?? '' };
      const mask: ServiceDaysMask = { ...(cur.serviceDays ?? shared.serviceDays ?? weekdayServiceDaysMask()) };
      mask[day] = !mask[day];
      return { ...prev, [routeId]: { ...cur, serviceDays: mask } };
    });
    setSaveNotice(null);
  }

  // Toggle same-vs-individual. When switching to individual, seed each mapped
  // route's override from the shared template so the cards start populated.
  function handleToggleSameForAll(next: boolean) {
    if (!next) {
      setSchedulesByRouteId((prev) => {
        const out = { ...prev };
        for (const routeId of mappedRouteIds) {
          if (!out[routeId]) {
            const slot = slotForRoute(routeId);
            out[routeId] = { ...shared, opsVehicleId: slot?.opsVehicleId ?? '' };
          }
        }
        return out;
      });
    }
    setSameForAll(next);
    setSaveNotice(null);
  }

  // ── Draft assembly + preview (per route) ───────────────────────────────────────
  // The operation is OPTIONAL when assigning to routes: you can attach vehicles +
  // a schedule now and pick/confirm the operation later (it's only strictly needed
  // when the upload actually creates shift solutions). So we build a draft per
  // mapped route regardless, with an empty operationSolutionId when none is chosen.
  const routeDrafts = React.useMemo(() => {
    const map: Record<string, RouteOperationAssignment> = {};
    for (const routeId of mappedRouteIds) {
      const a = assignmentForRoute(routeId);
      if (!a) continue;
      map[routeId] = {
        operationSolutionId: selectedOperationId || '',
        operationName: selectedOperation?.operationName,
        vehicleAssignments: [a],
        // Plan date range applied to every shift in this operation.
        dateRange: serviceDate
          ? { startDate: serviceDate, endDate: planEndDate || serviceDate }
          : undefined,
      };
    }
    return map;
  }, [mappedRouteIds, assignmentForRoute, selectedOperationId, selectedOperation, serviceDate, planEndDate]);

  // Preview across every drafted route (mapped vehicle + an operation selected).
  const draftedRouteIds = React.useMemo(() => Object.keys(routeDrafts), [routeDrafts]);
  const previewRows = React.useMemo(() => {
    const rows: ReturnType<typeof expandDraftToPreview> = [];
    for (const routeId of draftedRouteIds) {
      const r = routes.find((x) => x.id === routeId);
      if (!r) continue;
      const shadow: FixedRoute = { ...r, operationAssignments: [routeDrafts[routeId]] };
      rows.push(
        ...expandDraftToPreview(shadow, {
          serviceDate: serviceDate || undefined,
          endDate: planEndDate || undefined,
        }),
      );
    }
    return rows;
  }, [draftedRouteIds, routeDrafts, routes, serviceDate, planEndDate]);

  // Collapse per-day rows so each route's days show on one line when every other
  // field matches (the request carries no per-day distinction — PRD GAP B).
  const collapsedPreview = React.useMemo(() => collapsePreviewRows(previewRows), [previewRows]);

  // ── Save the draft onto each mapped route ───────────────────────────────────────
  function handleSaveDraft() {
    if (draftedRouteIds.length === 0) return;
    let saved = 0;
    let allHaveVersion = true;
    for (const routeId of draftedRouteIds) {
      const r = routes.find((x) => x.id === routeId);
      if (!r) continue;
      updateRoute({ ...r, operationAssignments: [routeDrafts[routeId]] });
      if (!r.uatRouteId && !r.uatMetadata?.originalId) allHaveVersion = false;
      saved += 1;
    }

    trackAnalyticsEvent('fixed_route_operations_plan_saved', {
      environment,
      network_id: selectedNetwork?.networkUuid ?? '',
      route_id: draftedRouteIds.join(','),
      operation_solution_id: selectedOperationId,
      vehicle_count: saved,
      shift_solution_preview_count: previewRows.length,
      has_route_version: allHaveVersion,
      timestamp: new Date().toISOString(),
    });

    setSaveNotice(
      `Assigned plan to ${saved} route${saved === 1 ? '' : 's'}, ` +
        `${previewRows.length} preview shift solution${previewRows.length === 1 ? '' : 's'}.`,
    );

    // Close on a successful assign — the plan is now persisted on the routes
    // (visible in each route's details, and used when you upload them). Leaving
    // the modal open made it look like nothing happened.
    onClose();
  }

  // Shared renderer for the schedule fields (days + times + shift params), reused
  // by the "same settings for all" template and each individual route card.
  const renderScheduleFields = (
    fields: SharedScheduleSettings,
    onPatch: (patch: Partial<SharedScheduleSettings>) => void,
    onToggleDay: (day: keyof ServiceDaysMask) => void,
    idPrefix: string,
  ) => {
    const mask = fields.serviceDays ?? weekdayServiceDaysMask();
    return (
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Operating days — {describeServiceDays(mask)}
          </Typography>
          <ToggleButtonGroup size="small" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
            {SERVICE_DAY_KEYS.map((day) => (
              <ToggleButton key={day} value={day} selected={mask[day]} onClick={() => onToggleDay(day)}>
                {SERVICE_DAY_SHORT_LABELS[day]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            label="Start"
            type="time"
            size="small"
            value={fields.startTime}
            onChange={(e) => onPatch({ startTime: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 120 }}
          />
          <TextField
            label="End"
            type="time"
            size="small"
            value={fields.endTime}
            onChange={(e) => onPatch({ endTime: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 120 }}
          />
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id={`${idPrefix}-dir`}>Direction</InputLabel>
            <Select
              labelId={`${idPrefix}-dir`}
              label="Direction"
              value={fields.direction}
              onChange={(e) => onPatch({ direction: e.target.value as ShiftSolutionDirection })}
              // Raise above the 2600 dialog (see MODAL_POPPER_SX).
              MenuProps={{ sx: MODAL_POPPER_SX }}
            >
              {DIRECTION_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id={`${idPrefix}-type`}>Shift type</InputLabel>
            <Select
              labelId={`${idPrefix}-type`}
              label="Shift type"
              value={fields.shiftType}
              onChange={(e) => onPatch({ shiftType: e.target.value as ShiftSolutionType })}
              // Raise above the 2600 dialog (see MODAL_POPPER_SX).
              MenuProps={{ sx: MODAL_POPPER_SX }}
            >
              {SHIFT_TYPE_OPTIONS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Start stop idx"
            type="number"
            size="small"
            value={fields.startStopIndex}
            onChange={(e) => onPatch({ startStopIndex: Math.max(0, Number(e.target.value) || 0) })}
            sx={{ width: 120 }}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="1st stop offset"
            type="number"
            size="small"
            value={fields.firstStopOffsetMins}
            onChange={(e) => onPatch({ firstStopOffsetMins: Number(e.target.value) || 0 })}
            sx={{ width: 130 }}
            InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
          />
          <TextField
            label="Repeats"
            type="number"
            size="small"
            value={fields.numberOfRepeats}
            onChange={(e) => onPatch({ numberOfRepeats: Math.max(0, Number(e.target.value) || 0) })}
            sx={{ width: 110 }}
            inputProps={{ min: 0 }}
          />
        </Stack>
      </Stack>
    );
  };

  if (routes.length === 0) return null;

  // Assigning to routes only needs at least one route mapped to a vehicle. The
  // operation is optional here (see routeDrafts) — it's a deploy-time concern.
  const canSave = mappedRouteIds.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      // The Data Transfer drawer (DataTransferDrawer.tsx) hoists its Paper to a
      // custom zIndex of 2201, well above MUI's default modal layer (1300), so a
      // default-zIndex Dialog renders BEHIND it. Sit clearly above the drawer
      // (and its 2305 menu/2500 JWT-dialog layers) so this modal — launched from
      // that drawer — is interactive. Theme-relative so it tracks MUI's scale.
      sx={{ zIndex: (t) => t.zIndex.modal + 1300 }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AltRouteOutlined fontSize="small" aria-hidden />
        Attach Vehicles & Operations
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {routes.length} route{routes.length === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Alert severity="info" icon={<WarningAmberOutlined fontSize="small" />}>
            This composes an operations plan <strong>assigned to the routes</strong>. Nothing is
            written to Ops here — the plan is applied when you upload these routes (Data Transfer →
            Routes).
          </Alert>

          {/* ── 1. Operation picker ─────────────────────────────────────────── */}
          <Stack spacing={1.5}>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <WorkOutlineOutlined fontSize="small" aria-hidden />
              Operation
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
              <TextField
                label="JWT token"
                type={showJwt ? 'text' : 'password'}
                value={jwtToken}
                onChange={(e) => handleJwtChange(e.target.value)}
                size="small"
                sx={{ flex: 1, minWidth: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowJwt((v) => !v)}
                        aria-label={showJwt ? 'Hide token' : 'Show token'}
                      >
                        {showJwt ? (
                          <VisibilityOffOutlined fontSize="small" />
                        ) : (
                          <VisibilityOutlined fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {/*
                When launched from the Data Transfer drawer (seedEnvironment set)
                the environment is LOCKED to the drawer's selection. The drawer
                governs the actual upload/deploy target, so letting this selector
                diverge would let a user map vehicles in one region (e.g. EUWE2
                UAT) and silently deploy them to another (UAT) — they don't exist
                there. One source of truth: change the environment in the drawer.
              */}
              <FormControl
                size="small"
                sx={{ minWidth: 130 }}
                disabled={Boolean(seedEnvironment)}
              >
                <InputLabel id="attach-ops-env-label">Environment</InputLabel>
                <Select
                  labelId="attach-ops-env-label"
                  value={environment}
                  label="Environment"
                  onChange={(e) => handleEnvironmentChange(e.target.value as Environment)}
                  // Raise above the 2600 dialog (see MODAL_POPPER_SX).
                  MenuProps={{ sx: MODAL_POPPER_SX }}
                >
                  {ENVIRONMENT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {Boolean(seedEnvironment) && (
                  <FormHelperText>Set by the Data Transfer drawer</FormHelperText>
                )}
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                disabled={!jwtToken.trim() || networksLoading}
                onClick={() => void handleLoadNetworks()}
                startIcon={
                  networksLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <HubOutlined fontSize="small" />
                  )
                }
                sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Load networks
              </Button>
            </Stack>

            {!jwtToken.trim() && (
              <Alert severity="info" icon={<WarningAmberOutlined fontSize="small" />} sx={{ py: 0.5 }}>
                No saved {getEnvironmentDisplayName(environment)} Ops token found. Paste a JWT to
                browse operation solutions (read-only).
              </Alert>
            )}

            {networks.length > 0 && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap>
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id="attach-ops-product-label">Product / network</InputLabel>
                  <Select
                    labelId="attach-ops-product-label"
                    label="Product / network"
                    value={selectedProductId}
                    onChange={(e) => {
                      setSelectedProductId(e.target.value);
                      setOperations([]);
                      setSelectedOperationId('');
                    }}
                    // Raise above the 2600 dialog (see MODAL_POPPER_SX).
                    MenuProps={{ sx: MODAL_POPPER_SX }}
                  >
                    {networks.map((n) => (
                      <MenuItem key={n.productId} value={n.productId}>
                        {n.productName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!selectedProductId || !serviceDate || operationsLoading}
                  onClick={() => void handleLoadOperations()}
                  startIcon={
                    operationsLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <DownloadOutlined fontSize="small" />
                    )
                  }
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0, alignSelf: 'flex-start' }}
                >
                  Load operations
                </Button>
              </Stack>
            )}

            {networks.length > 0 && selectedProductId && !serviceDate && (
              <Alert severity="info" icon={<CalendarMonthOutlined fontSize="small" />} sx={{ py: 0.5 }}>
                Set the <strong>plan start date</strong> in “Operating schedule” below to load
                operations for that date.
              </Alert>
            )}

            {fetchError && (
              <Alert severity="error" sx={{ py: 0.5 }}>
                {fetchError}
              </Alert>
            )}

            {operations.length > 0 && (
              <FormControl size="small" fullWidth>
                <InputLabel id="attach-ops-operation-label">Operation solution</InputLabel>
                <Select
                  labelId="attach-ops-operation-label"
                  label="Operation solution"
                  value={selectedOperationId}
                  onChange={(e) => setSelectedOperationId(e.target.value)}
                  // Raise above the 2600 dialog (see MODAL_POPPER_SX).
                  MenuProps={{ sx: MODAL_POPPER_SX }}
                >
                  {operations.map((op) => (
                    <MenuItem key={op.operationSolutionId} value={op.operationSolutionId}>
                      {op.operationName || op.operationSolutionId} ({op.startDate} → {op.endDate})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>

          <Divider />

          {/* ── 2. Vehicle mapping (panel renders its own "Vehicle slots" header) ── */}
          <Stack spacing={1.5}>
            <VehicleMappingPanel
              // No presets here: the modal already seeds one slot per route with a
              // stable `route:<id>` localId that ties the slot to its route. The
              // "One per route" preset created `route-<rand>` ids that broke that
              // linkage (mappedRouteIds counts only `route:` slots), leaving Assign
              // disabled even with vehicles mapped.
              slots={slots}
              onSlotsChange={setSlots}
              // This panel renders INSIDE the 2600 dialog, so its own Select
              // dropdowns (e.g. the per-slot Ops-vehicle picker) must clear it
              // too. Pass the same raised z-index used by this modal's Selects.
              menuZIndexSx={MODAL_POPPER_SX}
              // Embedded: reuse the Operation section's JWT / environment / network
              // so the user sets it once. Vehicles auto-load when a network is picked.
              auth={{
                jwtToken,
                environment,
                productId: selectedProductId,
                networkUuid:
                  selectedNetwork?.networkUuid ??
                  String(selectedNetwork?.defaultNetworkId ?? ''),
                networkLabel: selectedNetwork?.productName,
              }}
            />
          </Stack>

          <Divider />

          {/* ── 3. Operating schedule (same-for-all template / per-route) ───── */}
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
              <Typography
                variant="subtitle2"
                component="div"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}
              >
                <CalendarMonthOutlined fontSize="small" aria-hidden />
                Operating schedule
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={sameForAll}
                    onChange={(e) => handleToggleSameForAll(e.target.checked)}
                  />
                }
                label="Same settings for all routes"
              />
            </Stack>

            {/* Plan date range — applied to all vehicles/shifts/operations */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap>
              <TextField
                label="Plan start date"
                type="date"
                size="small"
                value={serviceDate}
                onChange={(e) => {
                  const next = e.target.value;
                  setServiceDate(next);
                  setPlanEndDate((prev) => (prev && prev < next ? next : prev || next));
                }}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 160 }}
              />
              <TextField
                label="Plan end date"
                type="date"
                size="small"
                value={planEndDate}
                onChange={(e) => setPlanEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: serviceDate || undefined }}
                error={Boolean(serviceDate && planEndDate && planEndDate < serviceDate)}
                helperText={
                  serviceDate && planEndDate && planEndDate < serviceDate
                    ? 'End is before start'
                    : undefined
                }
                sx={{ minWidth: 160 }}
              />
            </Stack>

            {sameForAll ? (
              <Box
                sx={{
                  p: 1.5,
                  border: `1px solid ${theme.palette.surface.borderSubtle}`,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}
                >
                  <DirectionsBusOutlined fontSize="inherit" aria-hidden />
                  {mappedRouteIds.length > 0
                    ? `Applied to all ${mappedRouteIds.length} mapped route${mappedRouteIds.length === 1 ? '' : 's'}.`
                    : 'Applied to each route once you map a vehicle above.'}
                </Typography>
                {renderScheduleFields(shared, updateShared, toggleSharedDay, 'shared')}
              </Box>
            ) : mappedRouteIds.length === 0 ? (
              <Alert severity="info" icon={<DirectionsBusOutlined fontSize="small" />} sx={{ py: 0.5 }}>
                Map at least one vehicle above to adjust routes individually.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {mappedRouteIds.map((routeId) => {
                  const r = routes.find((x) => x.id === routeId);
                  const a = assignmentForRoute(routeId);
                  if (!r || !a) return null;
                  const slot = slotForRoute(routeId);
                  return (
                    <Box
                      key={routeId}
                      sx={{
                        p: 1.5,
                        border: `1px solid ${theme.palette.surface.borderSubtle}`,
                        borderRadius: 1,
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Typography
                          variant="body2"
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontWeight: 600 }}
                        >
                          <AltRouteOutlined fontSize="small" aria-hidden />
                          {r.name}
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <DirectionsBusOutlined fontSize="inherit" aria-hidden />
                            {slot?.opsVehicleLabel || a.opsVehicleId}
                          </Typography>
                        </Typography>
                        {renderScheduleFields(
                          a,
                          (patch) => updateRouteSchedule(routeId, patch),
                          (day) => toggleRouteDay(routeId, day),
                          routeId,
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Stack>

          <Divider />

          {/* ── 4. Preview ──────────────────────────────────────────────────── */}
          <Stack spacing={1.5}>
            <Typography
              variant="subtitle2"
              component="div"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
            >
              <PreviewOutlined fontSize="small" aria-hidden />
              Preview — shift solutions that would be created
              <Chip
                size="small"
                variant="outlined"
                icon={<EventRepeatOutlined fontSize="small" />}
                label={`${collapsedPreview.length} shift solution${collapsedPreview.length === 1 ? '' : 's'}`}
                sx={{ ml: 1 }}
              />
            </Typography>

            {previewRows.length === 0 ? (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Pick an operation and map at least one vehicle (with operating days) to see the plan.
              </Alert>
            ) : (
              <TableContainer
                sx={{ border: `1px solid ${theme.palette.surface.borderSubtle}`, borderRadius: 1 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.surface.elevated, 0.6) }}>
                      <TableCell>Route</TableCell>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Days</TableCell>
                      <TableCell>Dates in operation</TableCell>
                      <TableCell>Direction</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>End</TableCell>
                      <TableCell align="right">Repeats</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {collapsedPreview.map((row) => {
                      const slot = slots.find((s) => s.opsVehicleId === row.opsVehicleId);
                      const startServiceDate = row.request.startDate
                        ? extractServiceDate(row.request.startDate)
                        : '';
                      const endServiceDate = row.request.endDate
                        ? extractServiceDate(row.request.endDate)
                        : '';
                      // Always show a start AND end date (a range), even when equal.
                      const datesInOperation =
                        !startServiceDate || !endServiceDate
                          ? '—'
                          : `${startServiceDate} → ${endServiceDate}`;
                      return (
                      <TableRow key={row.key} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell>{row.routeName}</TableCell>
                        <TableCell>{slot?.opsVehicleLabel || row.opsVehicleId}</TableCell>
                        <TableCell>{row.daysLabel}</TableCell>
                        <TableCell>{datesInOperation}</TableCell>
                        <TableCell>{row.request.direction}</TableCell>
                        <TableCell>{row.request.shiftType}</TableCell>
                        <TableCell>{(row.request.startDate && extractTimeFromDateTime(row.request.startDate)) || '—'}</TableCell>
                        <TableCell>{(row.request.endDate && extractTimeFromDateTime(row.request.endDate)) || '—'}</TableCell>
                        <TableCell align="right">{row.request.numberOfRepeats}</TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {saveNotice && (
              <Alert severity="success" icon={<SaveOutlined fontSize="small" />} sx={{ py: 0.5 }}>
                {saveNotice}
              </Alert>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ flex: 1 }}>
          {!canSave && (
            <Typography variant="caption" color="text.secondary">
              Map at least one vehicle to a route to assign.
            </Typography>
          )}
        </Box>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSaveDraft}
          disabled={!canSave}
          startIcon={<AltRouteOutlined fontSize="small" />}
        >
          Assign plan to routes
        </Button>
      </DialogActions>
    </Dialog>
  );
};
